import { put, takeLatest, call } from "redux-saga/effects";
import {
	setListPurchase,
	setDetailPurchase,
	GetListPurchaseAsyncPayload,
	getListPurchaseAsync,
	GetDetailPurchaseAsyncPayload,
	getDetailPurchaseAsync,
	UpdatePurchaseAsyncPayload,
	updatePurchaseAsync,
	createPurchaseAsync,
	CreatePurchaseAsyncPayload,
	setLoadingPurchase,
	ApplyVoucherCodePurchaseAsyncPayload,
	applyVoucherCodePurchaseAsyncAsync,
	DeleteVoucherCodePurchaseAsyncPayload,
	deleteVoucherCodePurchaseAsyncAsync,
	AddServiceAsyncPayload,
	addServiceAsync,
	DeleteServiceAsyncPayload,
	deleteServiceAsync,
} from "./purchase.slice";
import { message } from "antd";
import api from "service/apiService";
import { t } from "@lingui/macro";
import { translate } from "utils";
import { PayloadAction } from "@reduxjs/toolkit";
import { CREATED_SUCCESS } from "constants/messages";
import { PURCHASE_ORDER_TYPE } from "../types";

const urlParams = new URLSearchParams(window.location.search);

// Get list
function* getListPurchaseSaga(
	action: PayloadAction<GetListPurchaseAsyncPayload>,
) {
	const { query, callback } = action.payload;

	const { response, error } = yield call(async () => {
		let { skip } = query;
		const { take } = query;
		skip = skip * take;
		try {
			const { data: response } = await api.get("/dashboard/purchase-orders", {
				...query,
				skip,
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setListPurchase.type, payload: response });
	} else {
		message.error(translate(error.message));
	}

	callback && callback();
}
function* watchGetListPurchase() {
	yield takeLatest(getListPurchaseAsync, getListPurchaseSaga);
}

// Detail
function* getDetailPurchaseSaga(
	action: PayloadAction<GetDetailPurchaseAsyncPayload>,
) {
	yield put({ type: setLoadingPurchase.type, payload: true });
	const { id, query, onSuccess, isImportCar } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get(
				`/dashboard/purchase-orders/${id}${isImportCar ? "/import-car" : ""}`,
				query,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setDetailPurchase.type, payload: response });
		onSuccess?.(response.data);
	} else {
		message.error(translate(error.message));
	}
	yield put({ type: setLoadingPurchase.type, payload: false });
}
function* watchGetDetailPurchase() {
	yield takeLatest(getDetailPurchaseAsync, getDetailPurchaseSaga);
}

// Update
function* updatePurchaseSaga(
	action: PayloadAction<UpdatePurchaseAsyncPayload>,
) {
	const { id, data, onSuccess } = action.payload;
	yield put({ type: setLoadingPurchase.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const response = await api.patch(
				`/dashboard/purchase-orders/${id}`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		onSuccess();
	} else {
		message.error(translate(error.message));
	}
	yield put({ type: setLoadingPurchase.type, payload: false });
}
function* watchUpdatePurchase() {
	yield takeLatest(updatePurchaseAsync, updatePurchaseSaga);
}

// Create
function* createPurchaseSaga(
	action: PayloadAction<CreatePurchaseAsyncPayload>,
) {
	const { data, onSuccess, onFailure } = action.payload;
	yield put({ type: setLoadingPurchase.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post("dashboard/purchase-orders", data);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate(CREATED_SUCCESS));
		onSuccess();
	} else {
		onFailure && onFailure(error);
	}
	yield put({ type: setLoadingPurchase.type, payload: false });
}
function* watchCreatePurchase() {
	yield takeLatest(createPurchaseAsync, createPurchaseSaga);
}

// Apply voucher code to purchase
function* applyVoucherCodePurchaseSaga(
	action: PayloadAction<ApplyVoucherCodePurchaseAsyncPayload>,
) {
	const { id, voucherCode, onSuccess, onFailure } = action.payload;
	yield put({ type: setLoadingPurchase.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				`dashboard/purchase-orders/${id}/vouchers`,
				{ voucherCodes: [voucherCode] },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		onSuccess?.();
	} else {
		onFailure?.(error.code);
	}
}
function* watchApplyVoucherCodePurchase() {
	yield takeLatest(
		applyVoucherCodePurchaseAsyncAsync,
		applyVoucherCodePurchaseSaga,
	);
}

// Delete voucher code of purchase
function* deleteVoucherCodePurchaseSaga(
	action: PayloadAction<DeleteVoucherCodePurchaseAsyncPayload>,
) {
	const { id, voucherCode, onSuccess, onFailure } = action.payload;
	yield put({ type: setLoadingPurchase.type, payload: true });
	const { response, error } = yield call(async () => {
		try {
			const response = await api.delete(
				`dashboard/purchase-orders/${id}/vouchers/${voucherCode}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		onSuccess?.();
	} else {
		message.error(translate(error.code));
		onFailure?.(error.code);
	}
	yield put({ type: setLoadingPurchase.type, payload: false });
}
function* watchDeleteVoucherCodePurchase() {
	yield takeLatest(
		deleteVoucherCodePurchaseAsyncAsync,
		deleteVoucherCodePurchaseSaga,
	);
}

// Add service
function* addServiceSaga(action: PayloadAction<AddServiceAsyncPayload>) {
	const { purchaseOrderId, data, getResult, isImportCar } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post(
				`/dashboard/purchase-orders/${purchaseOrderId}/service-car`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Create successful`);
		yield put({
			type: getDetailPurchaseAsync.type,
			payload: { id: purchaseOrderId, isImportCar },
		});
		getResult({
			success: true,
		});
	} else {
		message.error(translate(error.message));
		getResult({
			success: false,
		});
	}
}
function* watchAddService() {
	yield takeLatest(addServiceAsync, addServiceSaga);
}

// Delete service
function* deleteServiceSaga(action: PayloadAction<DeleteServiceAsyncPayload>) {
	const { purchaseOrderId, serviceCarId, getResult, isImportCar } =
		action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.delete(
				`/dashboard/purchase-orders/${purchaseOrderId}/service-car/${serviceCarId}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(t`Delete successful`);
		yield put({
			type: getDetailPurchaseAsync.type,
			payload: { id: purchaseOrderId, isImportCar },
		});
		getResult({ success: true });
	} else {
		message.error(translate(error.message));
	}
}
function* watchDeleteService() {
	yield takeLatest(deleteServiceAsync, deleteServiceSaga);
}

const customerSaga = [
	watchGetListPurchase(),
	watchGetDetailPurchase(),
	watchUpdatePurchase(),
	watchCreatePurchase(),
	watchApplyVoucherCodePurchase(),
	watchDeleteVoucherCodePurchase(),
	watchAddService(),
	watchDeleteService(),
];
export default customerSaga;
